// Risk Calculator indicators. Scores run 1-5 where 5 is the HIGHEST enrollment
// risk; every measure is "lower is riskier", so each uses direction 'lower'
// with breakpoints ordered [score 5 at, score 4 at, score 3 at, score 2 at].
// Values past the score-2 cutoff floor at 1 (lowest risk).

export const RISK_CATEGORIES = [
  { id: 'enrollment', label: 'Enrollment', color: '#0f766e' },
  { id: 'entryCohort', label: 'Entry grade & cohorts', color: '#4338ca' },
  { id: 'quality', label: 'Academic quality', color: '#be123c' },
  { id: 'outlook', label: 'Outlook', color: '#b45309' },
];

export function riskCategoryColor(categoryId) {
  return RISK_CATEGORIES.find((category) => category.id === categoryId)?.color || '#64748b';
}

const LOWER = 'lower';
const STRICT = [false, false, false, false];
const HARD = 'hard';

const base = (fields) => ({ direction: LOWER, inclusive: STRICT, floorMode: HARD, ...fields });

export const RISK_INDICATORS = [
  base({
    id: 'enrollmentSize',
    label: 'Enrollment size (most recent year)',
    shortLabel: 'Enrollment',
    category: 'enrollment',
    sourceField: 'enrollment',
    breakpoints: [100, 200, 350, 500],
    defaultWeight: 0.1,
    format: 'integer',
  }),
  base({
    id: 'enrollmentChange5y',
    label: '5-year enrollment change',
    shortLabel: '5-yr Δ',
    category: 'enrollment',
    sourceField: 'enrollmentChange5y',
    breakpoints: [-0.2, -0.1, -0.05, 0],
    defaultWeight: 0.1,
    format: 'signedPercent',
  }),
  base({
    id: 'pctFromPeak',
    label: '% change from peak enrollment since 2019',
    shortLabel: '% from peak',
    category: 'enrollment',
    sourceField: 'pctFromPeak',
    breakpoints: [-0.3, -0.2, -0.1, -0.05],
    defaultWeight: 0.15,
    format: 'signedPercent',
  }),
  base({
    id: 'numFromPeak',
    label: 'Change from peak enrollment (students)',
    shortLabel: '# from peak',
    category: 'enrollment',
    sourceField: 'numFromPeak',
    breakpoints: [-200, -100, -50, -25],
    defaultWeight: 0.05,
    format: 'signedInteger',
  }),
  base({
    id: 'pctFromEntryPeak',
    label: '% change from peak entry-grade enrollment since 2019',
    shortLabel: 'Entry grade',
    category: 'entryCohort',
    sourceField: 'pctFromEntryPeak',
    breakpoints: [-0.4, -0.25, -0.15, -0.05],
    defaultWeight: 0.1,
    format: 'signedPercent',
  }),
  base({
    id: 'matriculationRate',
    label: 'Cohort matriculation rate (all grades)',
    shortLabel: 'Matriculation',
    category: 'entryCohort',
    sourceField: 'matriculationRate',
    breakpoints: [0.8, 0.85, 0.9, 0.95],
    defaultWeight: 0.15,
    format: 'percent',
  }),
  base({
    id: 'ela',
    label: 'ELA % meeting standard',
    shortLabel: 'ELA Proficiency',
    category: 'quality',
    sourceField: 'elaProficiency',
    breakpoints: [0.25, 0.35, 0.45, 0.55],
    defaultWeight: 0.08,
    format: 'percent',
  }),
  base({
    id: 'math',
    label: 'Math % meeting standard',
    shortLabel: 'Math Proficiency',
    category: 'quality',
    sourceField: 'mathProficiency',
    breakpoints: [0.15, 0.25, 0.35, 0.45],
    defaultWeight: 0.07,
    format: 'percent',
  }),
  base({
    id: 'areaPopChange',
    label: 'Area K-12 population projected change',
    shortLabel: 'Area pop.',
    category: 'outlook',
    sourceField: 'areaPopChange2031',
    breakpoints: [-0.05, -0.03, -0.01, 0],
    defaultWeight: 0.05,
    format: 'signedPercent',
  }),
  base({
    id: 'projectedChange5y',
    label: 'ProjectX midpoint 5-year projected change',
    shortLabel: 'ProjectX',
    category: 'outlook',
    sourceField: 'projectedChange5y',
    breakpoints: [-0.2, -0.1, -0.05, 0],
    defaultWeight: 0.15,
    format: 'signedPercent',
  }),
];

// Opt-in per dataset (`letterGradeIndicator`): binary A/B letter grade from
// the risk endpoint's `abQuality` (1 = A/B, 0 = C/D/F, null = unrated).
// Equal cutoffs make it all-or-nothing: C/D/F scores 5, A/B scores 1.
export const LETTER_GRADE_INDICATOR = base({
  id: 'letterGrade',
  label: 'A/B letter grade',
  shortLabel: 'Letter grade',
  category: 'quality',
  sourceField: 'abQuality',
  breakpoints: [0.5, 0.5, 0.5, 0.5],
  defaultWeight: 0.05,
  format: 'abBinary',
});

/**
 * Indicators for one location: the two academic-quality indicators (ELA and
 * Math % meeting standard) take their label, cutoffs and optional weight from
 * that state's assessment (see datasets/riskDatasets.js); a dataset with
 * `letterGradeIndicator` adds the A/B letter grade after Math. Labels pick up
 * the data vintages.
 */
export function buildRiskIndicators(dataset, meta = {}) {
  const qualityById = dataset?.qualityIndicators || {};
  const letterGrade = dataset?.letterGradeIndicator;
  const testYear = meta.proficiencyYear || meta.currentYear;
  const future = meta.esriFutureYear || '2031';
  const peakStart = String(meta.peakStartYear || '2019').slice(0, 4);
  return RISK_INDICATORS.flatMap((indicator) => {
    if (indicator.id === 'ela' || indicator.id === 'math') {
      const quality = qualityById[indicator.id] || {};
      const label = quality.label || indicator.label;
      const built = {
        ...indicator,
        label: testYear ? `${label} (${testYear})` : label,
        shortLabel: quality.shortLabel || indicator.shortLabel,
        breakpoints: quality.breakpoints || indicator.breakpoints,
        format: quality.format || indicator.format,
        defaultWeight: quality.defaultWeight ?? indicator.defaultWeight,
      };
      if (indicator.id === 'math' && letterGrade) {
        return [built, { ...LETTER_GRADE_INDICATOR, ...letterGrade }];
      }
      return [built];
    }
    if (indicator.id === 'enrollmentSize' && meta.currentYear) {
      return { ...indicator, label: `Enrollment size (${meta.currentYear})` };
    }
    if (indicator.id === 'areaPopChange') {
      return { ...indicator, label: `Area K-12 population projected change through ${future}` };
    }
    if (indicator.id === 'pctFromPeak') {
      return { ...indicator, label: `% change from peak enrollment since ${peakStart}` };
    }
    if (indicator.id === 'pctFromEntryPeak') {
      return { ...indicator, label: `% change from peak entry-grade enrollment since ${peakStart}` };
    }
    return indicator;
  });
}

// Columns shown before "Show all measures".
export const KEY_MEASURE_IDS = ['enrollmentSize', 'pctFromPeak', 'matriculationRate', 'ela', 'math', 'letterGrade', 'projectedChange5y'];
